import {Box, Typography} from "@mui/material";
import {
  Person as PersonIcon,
  Home as HomeIcon,
  FitnessCenter as FitnessIcon,
  Restaurant as RestaurantIcon,
  MonitorWeight as WeightIcon,
  MenuBook as ContentIcon,
} from "@mui/icons-material";

const FRAME_W = 240;
const FRAME_H = 480;

const BrandingPhonePreview = ({brandingColor, brandingAppName, logoUrl, backgroundImageUrl}) => {
  const color = brandingColor || "#07BBA6";

  return (
    <Box sx={{
      width: FRAME_W,
      height: FRAME_H,
      borderRadius: "36px",
      border: "6px solid #2a2a2a",
      backgroundColor: "#0f0f0f",
      overflow: "hidden",
      position: "relative",
      flexShrink: 0,
      boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    }}>
      {/* Background image layer */}
      {backgroundImageUrl && (
        <Box
          component="img"
          src={backgroundImageUrl}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.3,
          }}
        />
      )}

      {/* Dark overlay when no background */}
      {!backgroundImageUrl && (
        <Box sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "#171717",
        }} />
      )}

      {/* Content layer */}
      <Box sx={{
        position: "relative",
        zIndex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Notch */}
        <Box sx={{
          width: 80,
          height: 22,
          backgroundColor: "#2a2a2a",
          borderRadius: "0 0 16px 16px",
          margin: "0 auto",
        }} />

        {/* Status bar */}
        <Box sx={{display: "flex", justifyContent: "center", py: "2px"}}>
          <Typography sx={{color: "rgba(255,255,255,0.6)", fontSize: "9px", fontWeight: 600}}>
            9:41
          </Typography>
        </Box>

        {/* Header */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          px: "14px",
          py: "8px",
        }}>
          {/* Back/chevron placeholder */}
          <Box sx={{width: 24}} />

          {/* Center: logo or app name */}
          <Box sx={{flex: 1, display: "flex", justifyContent: "center", alignItems: "center"}}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                sx={{
                  height: 32,
                  maxWidth: 120,
                  objectFit: "contain",
                }}
              />
            ) : (
              <Typography sx={{
                color: color,
                fontSize: "13px",
                fontWeight: 700,
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 130,
              }}>
                {brandingAppName || "Progress"}
              </Typography>
            )}
          </Box>

          {/* Avatar */}
          <Box sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <PersonIcon sx={{color: "#fff", fontSize: "14px"}} />
          </Box>
        </Box>

        {/* Greeting */}
        <Box sx={{px: "14px", mb: "10px"}}>
          <Typography sx={{color: "rgba(255,255,255,0.5)", fontSize: "9px", fontWeight: 500}}>
            שלום
          </Typography>
          <Typography sx={{color: "#fff", fontSize: "12px", fontWeight: 700}}>
            User
          </Typography>
        </Box>

        {/* Glass cards */}
        <Box sx={{px: "12px", flex: 1}}>
          {/* Workout card */}
          <GlassCard>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px"}}>
              <Typography sx={{color: "#fff", fontSize: "9px", fontWeight: 600}}>
                האימון של היום
              </Typography>
              <FitnessIcon sx={{color: color, fontSize: "12px"}} />
            </Box>
            <Box sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: "rgba(255,255,255,0.1)",
              overflow: "hidden",
            }}>
              <Box sx={{
                width: "65%",
                height: "100%",
                borderRadius: 3,
                backgroundColor: color,
              }} />
            </Box>
            <Typography sx={{color: "rgba(255,255,255,0.4)", fontSize: "7px", mt: "4px"}}>
              3/5 תרגילים
            </Typography>
          </GlassCard>

          {/* Nutrition card */}
          <GlassCard>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: "6px"}}>
              <Typography sx={{color: "#fff", fontSize: "9px", fontWeight: 600}}>
                תזונה
              </Typography>
              <RestaurantIcon sx={{color: color, fontSize: "12px"}} />
            </Box>
            <Box sx={{display: "flex", gap: "8px"}}>
              <NutrientDot label="חלבון" value="120g" color={color} />
              <NutrientDot label="פחמימות" value="200g" color="rgba(255,255,255,0.3)" />
              <NutrientDot label="שומן" value="60g" color="rgba(255,255,255,0.3)" />
            </Box>
          </GlassCard>

          {/* Weight card */}
          <GlassCard>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <Typography sx={{color: "#fff", fontSize: "9px", fontWeight: 600}}>
                משקל
              </Typography>
              <Typography sx={{color: color, fontSize: "10px", fontWeight: 700}}>
                75.2 kg
              </Typography>
            </Box>
          </GlassCard>
        </Box>

        {/* Primary button */}
        <Box sx={{px: "12px", mb: "10px"}}>
          <Box sx={{
            backgroundColor: color,
            borderRadius: "48px",
            py: "7px",
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}>
            <Typography sx={{color: "#fff", fontSize: "10px", fontWeight: 700}}>
              התחל אימון
            </Typography>
          </Box>
        </Box>

        {/* Bottom nav */}
        <Box sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          py: "8px",
          px: "8px",
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}>
          <NavIcon icon={<HomeIcon />} active color={color} />
          <NavIcon icon={<FitnessIcon />} color={color} />
          <NavIcon icon={<RestaurantIcon />} color={color} />
          <NavIcon icon={<WeightIcon />} color={color} />
          <NavIcon icon={<ContentIcon />} color={color} />
        </Box>
      </Box>
    </Box>
  );
};

const GlassCard = ({children}) => (
  <Box sx={{
    backgroundColor: "rgba(169,169,169,0.2)",
    borderRadius: "10px",
    padding: "8px 10px",
    marginBottom: "8px",
  }}>
    {children}
  </Box>
);

const NutrientDot = ({label, value, color}) => (
  <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: "2px"}}>
    <Box sx={{width: 8, height: 8, borderRadius: "50%", backgroundColor: color}} />
    <Typography sx={{color: "rgba(255,255,255,0.5)", fontSize: "6px"}}>{label}</Typography>
    <Typography sx={{color: "#fff", fontSize: "7px", fontWeight: 600}}>{value}</Typography>
  </Box>
);

const NavIcon = ({icon, active, color}) => (
  <Box sx={{
    color: active ? color : "rgba(255,255,255,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& .MuiSvgIcon-root": {fontSize: "16px"},
  }}>
    {icon}
  </Box>
);

export default BrandingPhonePreview;
